import { X, Trash2, Receipt, ChevronDown, ChevronUp, Sparkles, Info, Calculator, Loader2 } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useBetSlip } from '../context/BetSlipContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Calculate accumulator odds
const calculateAccaOdds = (selections) => {
    return selections.reduce((acc, sel) => acc * sel.odds_decimal, 1);
};

// Calculate each-way returns
const calculateEachWayReturns = (stake, oddsDecimal, places = 3) => {
    const winStake = stake / 2;
    const placeStake = stake / 2;
    const placeOdds = 1 + ((oddsDecimal - 1) / 4); // 1/4 odds for places
    
    return {
        winPart: winStake * oddsDecimal,
        placePart: placeStake * placeOdds,
        total: (winStake * oddsDecimal) + (placeStake * placeOdds),
        placeOnly: placeStake * placeOdds
    };
};

// Single Selection Card Component
const SelectionCard = ({ 
    selection, 
    stake, 
    eachWay, 
    onStakeChange, 
    onEachWayChange, 
    onRemove,
    showEachWay = true 
}) => {
    const returns = eachWay 
        ? calculateEachWayReturns(stake, selection.odds_decimal)
        : { total: stake * selection.odds_decimal };
    
    // Check if race has started
    const now = new Date();
    const raceTime = selection.race_time ? new Date(selection.race_time) : 
                     selection.start_time ? new Date(selection.start_time) : null;
    const hasStarted = raceTime && raceTime <= now;
    
    return (
        <div 
            className={`rounded-lg p-4 animate-fade-in ${hasStarted ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted/50'}`}
            data-testid={`betslip-selection-${selection.entrant_id}`}
        >
            {hasStarted && (
                <div className="flex items-center gap-2 mb-2 text-red-400 text-xs">
                    <span className="font-semibold">⚠️ Race has started - remove to place bet</span>
                </div>
            )}
            <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${hasStarted ? 'text-red-400' : ''}`}>{selection.entrant_name}</p>
                    <p className="text-sm text-muted-foreground truncate">{selection.event_name}</p>
                    <p className="text-xs text-muted-foreground">{selection.stage_name}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                    <Badge className={`font-data text-sm ${hasStarted ? 'bg-red-500/20 text-red-400' : 'bg-primary/20 text-primary'}`}>
                        {selection.odds_fractional}
                    </Badge>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={onRemove}
                        data-testid={`remove-selection-${selection.entrant_id}`}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                        <Input
                            type="number"
                            placeholder="0.00"
                            min="0"
                            step="0.5"
                            value={stake || ''}
                            onChange={(e) => onStakeChange(parseFloat(e.target.value) || 0)}
                            className={`h-9 pl-7 ${hasStarted ? 'opacity-50' : ''}`}
                            disabled={hasStarted}
                            data-testid={`stake-input-${selection.entrant_id}`}
                        />
                    </div>
                </div>
                {showEachWay && !hasStarted && (
                    <div className="flex items-center gap-2">
                        <Switch
                            id={`ew-${selection.id}`}
                            checked={eachWay}
                            onCheckedChange={onEachWayChange}
                            data-testid={`ew-toggle-${selection.entrant_id}`}
                        />
                        <Label htmlFor={`ew-${selection.id}`} className="text-xs cursor-pointer">
                            E/W
                        </Label>
                    </div>
                )}
            </div>
            
            {stake > 0 && (
                <div className="mt-2 text-xs space-y-1">
                    {eachWay ? (
                        <>
                            <div className="flex justify-between text-muted-foreground">
                                <span>Win returns:</span>
                                <span className="font-data">£{returns.winPart.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                                <span>Place returns (1/4 odds):</span>
                                <span className="font-data">£{returns.placePart.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between font-medium text-primary">
                                <span>Total potential:</span>
                                <span className="font-data">£{returns.total.toFixed(2)}</span>
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-between text-muted-foreground">
                            <span>Returns:</span>
                            <span className="font-data text-primary font-medium">£{returns.total.toFixed(2)}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// AI Analysis Component
const AIAnalysis = ({ selections }) => {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const analyzeSelections = async () => {
        if (selections.length === 0) return;
        
        setLoading(true);
        try {
            // Create a summary of selections for AI analysis
            const selectionsData = selections.map(s => ({
                horse: s.entrant_name,
                race: s.event_name,
                venue: s.stage_name,
                odds: s.odds_fractional,
                odds_decimal: s.odds_decimal
            }));
            
            const response = await axios.post(`${API}/betslip/analyze`, {
                selections: selectionsData
            });
            
            setAnalysis(response.data);
        } catch (error) {
            console.error('Analysis failed:', error);
            // Provide fallback analysis
            const totalOdds = calculateAccaOdds(selections);
            const avgOdds = selections.reduce((sum, s) => sum + s.odds_decimal, 0) / selections.length;
            
            setAnalysis({
                summary: selections.length === 1 
                    ? `Single selection at ${selections[0].odds_fractional}. ${selections[0].odds_decimal > 5 ? 'Higher risk, higher reward pick.' : 'More likely to place, steady pick.'}`
                    : `${selections.length}-fold accumulator at combined odds of ${totalOdds.toFixed(2)}. ${totalOdds > 20 ? 'High-risk accumulator - consider smaller stakes.' : 'Moderate accumulator with reasonable combined odds.'}`,
                risk_level: totalOdds > 20 ? 'High' : totalOdds > 10 ? 'Medium' : 'Low',
                tip: selections.length > 3 
                    ? 'Consider splitting into smaller accumulators for better value.' 
                    : 'Each-way bets can provide insurance on longer-priced selections.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
                <Button 
                    variant="outline" 
                    className="w-full justify-between gap-2"
                    onClick={() => {
                        if (!isOpen && !analysis) {
                            analyzeSelections();
                        }
                        setIsOpen(!isOpen);
                    }}
                    data-testid="ai-analysis-btn"
                >
                    <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        AI Analysis
                    </span>
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <Card className="mt-2 border-purple-500/30 bg-purple-500/5">
                    <CardContent className="p-4">
                        {loading ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                                <span className="ml-2 text-sm">Analyzing your selections...</span>
                            </div>
                        ) : analysis ? (
                            <div className="space-y-3">
                                <p className="text-sm">{analysis.summary}</p>
                                <div className="flex items-center gap-2">
                                    <Badge 
                                        className={
                                            analysis.risk_level === 'High' ? 'bg-red-500/20 text-red-500' :
                                            analysis.risk_level === 'Medium' ? 'bg-amber-500/20 text-amber-500' :
                                            'bg-green-500/20 text-green-500'
                                        }
                                    >
                                        {analysis.risk_level} Risk
                                    </Badge>
                                </div>
                                {analysis.tip && (
                                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                                        <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                        <span>{analysis.tip}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Click to analyze your selections</p>
                        )}
                    </CardContent>
                </Card>
            </CollapsibleContent>
        </Collapsible>
    );
};

// Each-Way Explainer
const EachWayExplainer = () => {
    const [isOpen, setIsOpen] = useState(false);
    
    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <Info className="h-3 w-3" />
                What is Each-Way?
                {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs space-y-2">
                    <p>An <strong>Each-Way (E/W)</strong> bet is two bets in one:</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        <li><strong>Win part:</strong> Pays out at full odds if your horse wins</li>
                        <li><strong>Place part:</strong> Pays out at 1/4 odds if your horse places (usually top 3-4)</li>
                    </ul>
                    <p className="text-muted-foreground">
                        Your stake is doubled (half on win, half on place). Great for longer-priced selections!
                    </p>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

export const BetSlipDrawer = () => {
    const { selections, isOpen, setOpen, removeSelection, clearSlip, placeBet } = useBetSlip();
    const { isAuthenticated } = useAuth();
    const [stakes, setStakes] = useState({});
    const [eachWayBets, setEachWayBets] = useState({});
    const [accaStake, setAccaStake] = useState(0);
    const [accaEachWay, setAccaEachWay] = useState(false);
    const [isPlacing, setIsPlacing] = useState(false);
    const [betType, setBetType] = useState('singles');

    // Check if we have selections from different races (for accumulator)
    const uniqueRaces = useMemo(() => {
        const raceIds = new Set(selections.map(s => s.event_id));
        return raceIds.size;
    }, [selections]);

    const canAccumulate = uniqueRaces > 1 && uniqueRaces === selections.length;

    // Calculate totals for singles
    const singlesTotals = useMemo(() => {
        let totalStake = 0;
        let potentialReturn = 0;

        selections.forEach(sel => {
            const stake = stakes[sel.id] || 0;
            const isEW = eachWayBets[sel.id] || false;
            
            if (isEW) {
                totalStake += stake; // E/W doubles the stake
                const ewReturns = calculateEachWayReturns(stake, sel.odds_decimal);
                potentialReturn += ewReturns.total;
            } else {
                totalStake += stake;
                potentialReturn += stake * sel.odds_decimal;
            }
        });

        return { totalStake, potentialReturn };
    }, [selections, stakes, eachWayBets]);

    // Calculate accumulator odds and returns
    const accaTotals = useMemo(() => {
        if (!canAccumulate || selections.length < 2) return { odds: 0, potential: 0 };
        
        const combinedOdds = calculateAccaOdds(selections);
        
        if (accaEachWay) {
            const ewReturns = calculateEachWayReturns(accaStake, combinedOdds);
            return {
                odds: combinedOdds,
                potential: ewReturns.total,
                winPart: ewReturns.winPart,
                placePart: ewReturns.placePart
            };
        }
        
        return {
            odds: combinedOdds,
            potential: accaStake * combinedOdds
        };
    }, [selections, accaStake, accaEachWay, canAccumulate]);

    const handleStakeChange = (selectionId, value) => {
        setStakes(prev => ({ ...prev, [selectionId]: value }));
    };

    const handleEachWayChange = (selectionId, value) => {
        setEachWayBets(prev => ({ ...prev, [selectionId]: value }));
    };

    const handlePlaceBet = async () => {
        const totalStake = betType === 'singles' ? singlesTotals.totalStake : accaStake;
        if (totalStake === 0) return;
        
        setIsPlacing(true);
        try {
            await placeBet(
                betType === 'singles' ? stakes : { accumulator: accaStake },
                betType,
                betType === 'singles' ? eachWayBets : { accumulator: accaEachWay }
            );
            setStakes({});
            setEachWayBets({});
            setAccaStake(0);
            setAccaEachWay(false);
        } finally {
            setIsPlacing(false);
        }
    };

    return (
        <Sheet open={isOpen} onOpenChange={setOpen}>
            <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="betslip-drawer">
                <SheetHeader>
                    <SheetTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                            <Receipt className="h-5 w-5" />
                            Bet Slip
                            {selections.length > 0 && (
                                <Badge variant="secondary" className="font-data">
                                    {selections.length}
                                </Badge>
                            )}
                        </span>
                        {selections.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={clearSlip} data-testid="clear-betslip">
                                <Trash2 className="h-4 w-4 mr-1" />
                                Clear
                            </Button>
                        )}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-auto mt-4">
                    {selections.length === 0 ? (
                        <div className="text-center py-12">
                            <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">Your bet slip is empty</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Click on odds to add selections
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Bet Type Tabs */}
                            {canAccumulate ? (
                                <Tabs value={betType} onValueChange={setBetType}>
                                    <TabsList className="grid w-full grid-cols-2">
                                        <TabsTrigger value="singles" data-testid="singles-tab">
                                            Singles
                                        </TabsTrigger>
                                        <TabsTrigger value="accumulator" data-testid="acca-tab">
                                            {selections.length}-Fold Acca
                                        </TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="singles" className="space-y-3 mt-4">
                                        <EachWayExplainer />
                                        {selections.map((sel) => (
                                            <SelectionCard
                                                key={sel.id}
                                                selection={sel}
                                                stake={stakes[sel.id] || 0}
                                                eachWay={eachWayBets[sel.id] || false}
                                                onStakeChange={(v) => handleStakeChange(sel.id, v)}
                                                onEachWayChange={(v) => handleEachWayChange(sel.id, v)}
                                                onRemove={() => removeSelection(sel.id)}
                                            />
                                        ))}
                                    </TabsContent>

                                    <TabsContent value="accumulator" className="space-y-4 mt-4">
                                        {/* Accumulator Info */}
                                        <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-4 border border-primary/20">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="font-semibold flex items-center gap-2">
                                                    <Calculator className="h-4 w-4" />
                                                    {selections.length}-Fold Accumulator
                                                </h4>
                                                <Badge className="font-data text-lg bg-primary/20 text-primary">
                                                    {accaTotals.odds.toFixed(2)}
                                                </Badge>
                                            </div>
                                            
                                            {/* List selections */}
                                            <div className="space-y-1 mb-4">
                                                {selections.map((sel, idx) => (
                                                    <div key={sel.id} className="flex items-center justify-between text-sm">
                                                        <span className="text-muted-foreground truncate flex-1">
                                                            {idx + 1}. {sel.entrant_name}
                                                        </span>
                                                        <span className="font-data ml-2">{sel.odds_fractional}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Stake Input */}
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1">
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                                                        <Input
                                                            type="number"
                                                            placeholder="0.00"
                                                            min="0"
                                                            step="0.5"
                                                            value={accaStake || ''}
                                                            onChange={(e) => setAccaStake(parseFloat(e.target.value) || 0)}
                                                            className="h-10 pl-7"
                                                            data-testid="acca-stake-input"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        id="acca-ew"
                                                        checked={accaEachWay}
                                                        onCheckedChange={setAccaEachWay}
                                                        data-testid="acca-ew-toggle"
                                                    />
                                                    <Label htmlFor="acca-ew" className="text-sm cursor-pointer">
                                                        E/W
                                                    </Label>
                                                </div>
                                            </div>

                                            {/* Returns */}
                                            {accaStake > 0 && (
                                                <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                                                    {accaEachWay ? (
                                                        <>
                                                            <div className="flex justify-between text-sm text-muted-foreground">
                                                                <span>Win returns:</span>
                                                                <span className="font-data">£{accaTotals.winPart?.toFixed(2)}</span>
                                                            </div>
                                                            <div className="flex justify-between text-sm text-muted-foreground">
                                                                <span>Place returns:</span>
                                                                <span className="font-data">£{accaTotals.placePart?.toFixed(2)}</span>
                                                            </div>
                                                        </>
                                                    ) : null}
                                                    <div className="flex justify-between font-semibold">
                                                        <span>Potential Returns:</span>
                                                        <span className="font-data text-primary text-lg">
                                                            £{accaTotals.potential.toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <p className="text-xs text-muted-foreground text-center">
                                            All selections must win for the accumulator to pay out.
                                            {accaEachWay && ' E/W accumulators pay if all selections place.'}
                                        </p>
                                    </TabsContent>
                                </Tabs>
                            ) : (
                                /* Single Selection Mode */
                                <div className="space-y-3">
                                    <EachWayExplainer />
                                    {selections.map((sel) => (
                                        <SelectionCard
                                            key={sel.id}
                                            selection={sel}
                                            stake={stakes[sel.id] || 0}
                                            eachWay={eachWayBets[sel.id] || false}
                                            onStakeChange={(v) => handleStakeChange(sel.id, v)}
                                            onEachWayChange={(v) => handleEachWayChange(sel.id, v)}
                                            onRemove={() => removeSelection(sel.id)}
                                        />
                                    ))}
                                    {selections.length > 1 && uniqueRaces < selections.length && (
                                        <p className="text-xs text-amber-500 bg-amber-500/10 p-2 rounded">
                                            Multiple selections from the same race - accumulator not available.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* AI Analysis */}
                            {selections.length > 0 && (
                                <AIAnalysis selections={selections} />
                            )}
                        </div>
                    )}
                </div>

                {/* Footer with totals and place bet */}
                {selections.length > 0 && (
                    <div className="border-t pt-4 mt-4 space-y-4">
                        <Separator />
                        
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Stake</span>
                                <span className="font-data font-semibold">
                                    £{(betType === 'singles' ? singlesTotals.totalStake : accaStake).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Potential Returns</span>
                                <span className="font-data text-xl font-bold text-primary">
                                    £{(betType === 'singles' ? singlesTotals.potentialReturn : accaTotals.potential).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        <Button
                            className="w-full btn-pill h-12 text-lg"
                            disabled={
                                (betType === 'singles' ? singlesTotals.totalStake : accaStake) === 0 || 
                                isPlacing || 
                                !isAuthenticated
                            }
                            onClick={handlePlaceBet}
                            data-testid="place-bet-btn"
                        >
                            {isPlacing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Placing...
                                </>
                            ) : (
                                `Place ${betType === 'accumulator' ? 'Accumulator' : 'Bet'} (Demo)`
                            )}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground">
                            This is a demo platform. No real money is involved.
                        </p>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
